import {
  InputHTMLAttributes,
  ReactElement,
  forwardRef,
  isValidElement,
  memo,
  useRef,
  useState,
} from 'react'

import Input, { BasicInputProps } from '@/components/GenericInput'
import GenericOption, { OptionsType } from '@/components/GenericOption'
import { useOnClickOutside } from '@/lib/hooks/useOnClickOutside'
import { classNames } from '@/lib/misc/Utils'
import InputSearch from '@/components/InputSearch'
import ChevronDownIcon from '@icons/chevron-down'
import CloseIcon from '@icons/x-mark'

interface SelectInputProps
  extends
    Omit<BasicInputProps, 'value'>,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
  options: OptionsType[]
  setValue: (val: any, option?: OptionsType) => void
  onReset?: () => void
  onClear?: () => void
  icon?: ReactElement
  isSmall?: boolean
  suffix?: ReactElement
  value?: string | number | readonly string[] | ReactElement
}

interface GenericSelectProps extends SelectInputProps {
  inputType?: 'standard' | 'search'
}

const GenericSelect = forwardRef<HTMLInputElement, GenericSelectProps>(
  (
    {
      cy,
      className,
      options,
      inputType = 'standard',
      setValue,
      onReset,
      onClear,
      icon,
      isSmall,
      ...props
    },
    ref
  ) => {
    const [isOpened, setIsOpened] = useState(false)
    const containerRef = useRef(null)

    useOnClickOutside(containerRef, () => {
      setIsOpened(false)
    })

    const onSelectOptionHandler = (val: any, option?: OptionsType) => {
      setValue(val, option)
      setIsOpened(false)
    }

    const onClearHandler = () => {
      if (onClear) {
        onClear()
      }
    }

    return (
      <div className={classNames('relative', className)} ref={containerRef}>
        {inputType === 'standard' ? (
          props.readOnly && isValidElement(props.value) ? (
            <div
              className={classNames(
                'h-full w-full px-3 border bg-white rounded-lg border-solid border-neutral-70 hover:border-neutral-40 cursor-default flex items-center',
                isSmall ? 'py-2' : 'py-3'
              )}
              onClick={() => setIsOpened(true)}
              data-cy={cy}
            >
              {props.value}
            </div>
          ) : (
            <Input
              {...props}
              value={
                typeof props.value === 'string' ||
                typeof props.value === 'number' ||
                Array.isArray(props.value)
                  ? props.value
                  : undefined
              }
              cy={cy}
              ref={ref}
              onFocus={() => setIsOpened(true)}
              onClick={() => setIsOpened(true)}
              onChangeCapture={() => setIsOpened(true)}
              autoComplete="off"
              style={props.readOnly ? { cursor: 'default' } : {}}
              icon={icon}
              isSmall={isSmall}
            />
          )
        ) : (
          <InputSearch
            {...props}
            value={
              typeof props.value === 'string' ||
              typeof props.value === 'number' ||
              Array.isArray(props.value)
                ? props.value
                : undefined
            }
            cy={cy}
            ref={ref}
            showSearchIcon={!options?.length}
            onFocus={() => setIsOpened(true)}
            onClick={() => setIsOpened(true)}
            onChangeCapture={() => setIsOpened(true)}
            onReset={() => {
              if (onReset) {
                onReset()
              }
            }}
          />
        )}
        {!!options?.length && (inputType !== 'search' || !props.value) && (
          <div className="absolute -translate-y-2/4 right-3 top-2/4 z-20 flex items-center gap-1">
            {props.value && onClear && (
              <CloseIcon
                className="text-neutral-40 cursor-pointer hover:text-neutral-60"
                onClick={onClearHandler}
                data-cy="select-clear-icon"
              />
            )}
            <ChevronDownIcon
              onClick={() => setIsOpened(!isOpened)}
              className={classNames(
                'text-neutral-40 transition-transform duration-[0.2s] ease-[ease-in-out] rotate-0',
                isOpened && '-rotate-180!'
              )}
            />
          </div>
        )}
        {isOpened && !!options?.length && (
          <GenericOption
            className="max-h-[170px] overflow-auto absolute right-0 left-0 top-[110%] z-30 w-full shadow-xl bg-white border rounded-lg border-solid border-neutral-20"
            cy={cy}
            options={options}
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            onSelectOption={onSelectOptionHandler}
          />
        )}
      </div>
    )
  }
)

const Select = memo(
  forwardRef<HTMLInputElement, SelectInputProps>((props, ref) => (
    <GenericSelect {...props} readOnly ref={ref} />
  ))
)

const Autocomplete = memo(
  forwardRef<HTMLInputElement, SelectInputProps>((props, ref) => (
    <GenericSelect {...props} ref={ref} />
  ))
)

const SearchInput = memo(
  forwardRef<HTMLInputElement, SelectInputProps>((props, ref) => (
    <GenericSelect {...props} inputType="search" ref={ref} />
  ))
)

export { Select, Autocomplete, SearchInput }
export type { GenericSelectProps }
